import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import MonthlyTasksGrid from '../components/MonthlyTasksGrid';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  BarChart,
  Bar,
  ReferenceLine,
  Cell
} from 'recharts';

export default function Analytics({ days = 30 }) {
  const [averageTimeData, setAverageTimeData] = useState([]);
  const [tasksClosedData, setTasksClosedData] = useState([]);
  const [popularListsData, setPopularListsData] = useState([]);
  const [taskAgeData, setTaskAgeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Detect current theme
  const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [avgTimeResponse, tasksClosedResponse, popularListsResponse, taskAgeResponse] = await Promise.all([
        api.getAverageTimeToClose(days),
        api.getTasksClosedOverTime(days),
        api.getPopularListsOverTime(days),
        api.getTaskAgeDistribution()
      ]);

      setAverageTimeData(avgTimeResponse.data || []);
      setTasksClosedData(tasksClosedResponse.data || []);
      setPopularListsData(popularListsResponse.data || []);
      setTaskAgeData(taskAgeResponse.data || []);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError('Failed to load analytics data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  // Get unique list names for legend
  const getListNames = () => {
    const names = new Set();
    popularListsData.forEach(item => {
      if (item.list_name) {
        names.add(item.list_name);
      } else if (item.list_id) {
        names.add(item.list_id);
      }
    });
    return Array.from(names);
  };

  const listNames = getListNames();

  // Theme-aware UI colors for axes, grids, and text
  const uiColors = {
    grid: isDarkMode ? '#334155' : '#f1f5f9',
    axis: isDarkMode ? '#94a3b8' : '#64748b',
    axisLabel: isDarkMode ? '#94a3b8' : '#64748b',
    error: isDarkMode ? '#fca5a5' : '#ef4444'
  };
  
  // Enterprise analytics color palette - consistent across all charts
  const chartColors = {
    primary: '#2563eb',      // Professional blue
    secondary: '#1e40af',    // Darker blue
    accent: '#3b82f6',       // Lighter blue
    success: '#059669',       // Green for positive metrics
    neutral: '#64748b',      // Gray for secondary data
  };

  const gradientColors = {
    primary: ['#2563eb', '#1e40af'],
    secondary: ['#3b82f6', '#2563eb'],
    success: ['#059669', '#047857'],
  };

  // Consistent color palette for multiple series - vibrant, distinct colors (no grays)
  const seriesColors = [
    '#a78bfa',  // Pastel purple
    '#86efac',  // Pastel green
    '#fbbf24',  // Amber
    '#fb7185',  // Rose
    '#7dd3fc',  // Sky blue
    '#fda4af',  // Pink
    '#c4b5fd',  // Lavender
    '#6ee7b7',  // Mint
    '#fcd34d',  // Yellow
    '#60a5fa',  // Blue
    '#f472b6',  // Hot pink
    '#a3e635',  // Lime
    '#fb923c',  // Orange
    '#818cf8',  // Indigo
    '#34d399',  // Emerald
    '#facc15',  // Yellow-amber
    '#f87171',  // Red
    '#2dd4bf',  // Teal
    '#c084fc',  // Purple
    '#22d3ee',  // Cyan
  ];

  // Create a color map for lists
  const listColorMap = {};
  listNames.forEach((name, index) => {
    listColorMap[name] = seriesColors[index % seriesColors.length];
  });

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div className="spinner"></div>
        <p className="loading-text">Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <p style={{ color: uiColors.error }}>{error}</p>
        <button
          onClick={loadAnalytics}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            backgroundColor: 'var(--primary-color)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // Calculate average tasks completed
  const averageTasksCompleted = tasksClosedData.length > 0
    ? tasksClosedData.reduce((sum, item) => sum + item.count, 0) / tasksClosedData.length
    : 0;

  return (
    <div style={{ padding: '1rem 2rem 2rem 2rem', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ display: 'grid', gap: '2rem', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))' }}>
        {/* Tasks Closed Over Time Chart */}
        <div style={{
          backgroundColor: 'var(--bg-card)',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid var(--border-color)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
        }}
        >
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              Tasks Completed
            </h3>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              Number of tasks completed per day
              {averageTasksCompleted > 0 && (
                <span style={{ marginLeft: '0.5rem', fontWeight: 500, color: chartColors.success }}>
                  • Avg: {averageTasksCompleted.toFixed(1)} tasks/day
                </span>
              )}
            </p>
          </div>
          {tasksClosedData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={tasksClosedData} margin={{ top: 10, right: 70, left: 20, bottom: 0 }}>
                <defs>
                  <linearGradient id="tasksClosedGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={gradientColors.primary[0]} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={gradientColors.primary[0]} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={uiColors.grid} vertical={false} />
                <XAxis
                  dataKey="date"
                  stroke={uiColors.axis}
                  style={{ fontSize: '0.75rem', fontWeight: 500 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis
                  stroke={uiColors.axis}
                  style={{ fontSize: '0.75rem', fontWeight: 500 }}
                  tickLine={false}
                  axisLine={false}
                  label={{ value: 'Tasks', angle: -90, position: 'insideLeft', offset: 10, style: { textAnchor: 'middle', fill: uiColors.axisLabel, fontWeight: 500 } }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                  }}
                  labelStyle={{ fontWeight: 600, marginBottom: '4px', color: 'var(--text-primary)' }}
                  itemStyle={{ color: chartColors.primary, fontWeight: 500 }}
                  labelFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  }}
                  formatter={(value, name) => {
                    if (name === 'Average') {
                      return [`${averageTasksCompleted.toFixed(1)} tasks`, 'Average'];
                    }
                    return [value, 'Tasks Closed'];
                  }}
                />
                <ReferenceLine
                  y={averageTasksCompleted}
                  stroke={chartColors.success}
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  label={{
                    value: `Avg: ${averageTasksCompleted.toFixed(1)}`,
                    position: 'right',
                    fill: chartColors.success,
                    fontSize: 12,
                    fontWeight: 600
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke={chartColors.primary}
                  strokeWidth={3}
                  fill="url(#tasksClosedGradient)"
                  dot={{ fill: chartColors.primary, r: 4, strokeWidth: 2, stroke: 'white' }}
                  activeDot={{ r: 6, strokeWidth: 2, stroke: 'white', fill: chartColors.primary }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</div>
              <p style={{ margin: 0 }}>No data available for this period</p>
            </div>
          )}
        </div>

        {/* Monthly Tasks Calendar Grid */}
        <div style={{
          backgroundColor: 'var(--bg-card)',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid var(--border-color)',
          gridColumn: '1 / -1',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
        }}
        >
          {popularListsData.length > 0 ? (
            <MonthlyTasksGrid tasksData={popularListsData} listColors={listColorMap} />
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📅</div>
              <p style={{ margin: 0 }}>No task data available for this month</p>
            </div>
          )}
        </div>

        {/* Tasks by List This Month - Horizontal Bar Chart */}
        <div style={{
          backgroundColor: 'var(--bg-card)',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid var(--border-color)',
          gridColumn: '1 / -1',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
        }}
        >
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              Tasks Completed by List This Month
            </h3>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              Total tasks completed per list in the current month
            </p>
          </div>
          {(() => {
            // Get current month in Pacific time
            const now = new Date();
            const formatter = new Intl.DateTimeFormat('en-US', {
              timeZone: 'America/Los_Angeles',
              year: 'numeric',
              month: 'numeric'
            });
            const parts = formatter.formatToParts(now);
            const currentYear = parseInt(parts.find(p => p.type === 'year').value);
            const currentMonth = parseInt(parts.find(p => p.type === 'month').value);
            const currentMonthStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
            
            // Filter data for current month and aggregate by list
            const listTotals = {};
            popularListsData.forEach(item => {
              if (item.date && item.date.startsWith(currentMonthStr)) {
                const listName = item.list_name || item.list_id || 'Unknown';
                if (!listTotals[listName]) {
                  listTotals[listName] = 0;
                }
                listTotals[listName] += item.task_count;
              }
            });
            
            // Convert to array and sort by count descending
            const chartData = Object.entries(listTotals)
              .map(([name, count]) => ({ name, count }))
              .sort((a, b) => b.count - a.count);
            
            return chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 50)}>
                <BarChart 
                  data={chartData} 
                  layout="vertical"
                  margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={uiColors.grid} horizontal={false} />
                  <XAxis 
                    type="number"
                    stroke={uiColors.axis}
                    style={{ fontSize: '0.75rem', fontWeight: 500 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    type="category"
                    dataKey="name"
                    stroke={uiColors.axis}
                    style={{ fontSize: '0.875rem', fontWeight: 500 }}
                    tickLine={false}
                    axisLine={false}
                    width={150}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--bg-card)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      padding: '12px',
                      boxShadow: 'var(--shadow-md)'
                    }}
                    labelStyle={{ fontWeight: 600, marginBottom: '4px', color: 'var(--text-primary)' }}
                    formatter={(value) => [value, 'Tasks Completed']}
                  />
                  <Bar 
                    dataKey="count"
                    radius={[0, 8, 8, 0]}
                  >
                    {chartData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`}
                        fill={listColorMap[entry.name] || seriesColors[index % seriesColors.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</div>
                <p style={{ margin: 0 }}>No completed tasks this month</p>
              </div>
            );
          })()}
        </div>

        {/* Average Time to Close Chart */}
        <div style={{
          backgroundColor: 'var(--bg-card)',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid var(--border-color)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
        }}
        >
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              Average Time to Close
            </h3>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              Average hours from task creation to completion
            </p>
          </div>
          {averageTimeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={averageTimeData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="avgTimeGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={gradientColors.primary[0]} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={gradientColors.primary[0]} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={uiColors.grid} vertical={false} />
                <XAxis
                  dataKey="date"
                  stroke={uiColors.axis}
                  style={{ fontSize: '0.75rem', fontWeight: 500 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis
                  stroke={uiColors.axis}
                  style={{ fontSize: '0.75rem', fontWeight: 500 }}
                  tickLine={false}
                  axisLine={false}
                  label={{ value: 'Hours', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: uiColors.axisLabel, fontWeight: 500 } }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                  }}
                  labelStyle={{ fontWeight: 600, marginBottom: '4px', color: 'var(--text-primary)' }}
                  itemStyle={{ color: chartColors.primary, fontWeight: 500 }}
                  labelFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  }}
                  formatter={(value) => [`${Number(value).toFixed(1)} hours`, 'Average']}
                />
                <Area
                  type="monotone"
                  dataKey="average_hours"
                  stroke={chartColors.primary}
                  strokeWidth={3}
                  fill="url(#avgTimeGradient)"
                  dot={{ fill: chartColors.primary, r: 4, strokeWidth: 2, stroke: 'white' }}
                  activeDot={{ r: 6, strokeWidth: 2, stroke: 'white', fill: chartColors.primary }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</div>
              <p style={{ margin: 0 }}>No data available for this period</p>
            </div>
          )}
        </div>

        {/* Task Age Distribution Histogram */}
        <div style={{
          backgroundColor: 'var(--bg-card)',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid var(--border-color)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
        }}
        >
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              Task Age Distribution
            </h3>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              Distribution of incomplete tasks by age (days since creation)
            </p>
          </div>
          {taskAgeData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={taskAgeData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                <defs>
                  <linearGradient id="ageGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={gradientColors.primary[0]} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={gradientColors.primary[1]} stopOpacity={0.6}/>
                  </linearGradient>
                </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={uiColors.grid} vertical={false} />
                  <XAxis
                    dataKey="age_range"
                    stroke={uiColors.axis}
                    style={{ fontSize: '0.75rem', fontWeight: 500 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke={uiColors.axis}
                    style={{ fontSize: '0.75rem', fontWeight: 500 }}
                    tickLine={false}
                    axisLine={false}
                    label={{ value: 'Tasks', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: uiColors.axisLabel, fontWeight: 500 } }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--bg-card)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      padding: '12px',
                      boxShadow: 'var(--shadow-md)'
                    }}
                  labelStyle={{ fontWeight: 600, marginBottom: '4px', color: 'var(--text-primary)' }}
                  itemStyle={{ color: chartColors.primary, fontWeight: 500 }}
                  labelFormatter={(value) => `${value} days`}
                  formatter={(value) => [value, 'Tasks']}
                />
                <Bar 
                  dataKey="count" 
                  fill="url(#ageGradient)"
                  radius={[8, 8, 0, 0]}
                  stroke={chartColors.primary}
                  strokeWidth={1}
                />
                </BarChart>
              </ResponsiveContainer>
              <div style={{ textAlign: 'center', marginTop: '0.25rem' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 500, color: uiColors.axisLabel }}>Age (days)</span>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</div>
              <p style={{ margin: 0 }}>No data available</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}


